/**
 * Twinkle Cart — suggested accessories in drawer
 * @module cart-associated-products
 */
class CartAssociatedProducts extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.init = this.init.bind(this);
    this.onAccessorySubmit = this.onAccessorySubmit.bind(this);
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }

  init() {
    this.initSlider();
    this.bindAccessoryForms();
  }

  initSlider() {
    if (this.swiper) return;
    const container = this.querySelector('.cart__upsell_slider');
    if (!container) return;

    if (!window.Swiper) {
      window.addEventListener('load', () => this.initSlider(), { once: true });
      return;
    }

    const paginationEl = this.querySelector('.upsell_pagination');
    const prevEl = this.querySelector('.upsell-prev');
    const nextEl = this.querySelector('.upsell-next');

    this.swiper = new Swiper(container, {
      spaceBetween: 40,
      speed: 500,
      slidesPerView: 1,
      loop: true,
      centeredSlides: true,
      pagination: paginationEl ? { el: paginationEl, clickable: true } : undefined,
      navigation: prevEl || nextEl ? { prevEl, nextEl } : undefined,
    });
  }

  bindAccessoryForms() {
    this.querySelectorAll('[data-associated-accessory-form]').forEach((form) => {
      if (form.dataset.accessoryBound === 'true') return;
      form.dataset.accessoryBound = 'true';
      form.addEventListener('submit', this.onAccessorySubmit);
    });
  }

  setButtonLoading(form, isLoading) {
    const button = form.querySelector('button[type="submit"]');
    const spinner = form.querySelector('.loading-overlay__spinner');
    if (!button) return;

    button.toggleAttribute('disabled', isLoading);
    button.setAttribute('aria-disabled', isLoading ? 'true' : 'false');
    if (spinner) spinner.classList.toggle('hidden', !isLoading);
  }

  cloneProperties(properties) {
    const cloned = {};
    if (!properties || typeof properties !== 'object') return cloned;
    Object.keys(properties).forEach((key) => {
      cloned[key] = properties[key];
    });
    return cloned;
  }

  buildNextProperties(currentProperties, accessoryHandle, accessoryVariantId) {
    const properties = this.cloneProperties(currentProperties);
    const accessoriesRaw = properties.Accessories;
    const accessories =
      accessoriesRaw && typeof accessoriesRaw === 'object' && !Array.isArray(accessoriesRaw)
        ? this.cloneProperties(accessoriesRaw)
        : {};

    const currentQty = parseInt(accessories[accessoryHandle], 10) || 0;
    accessories[accessoryHandle] = currentQty + 1;
    properties.Accessories = accessories;

    let pdpAddonUpdated = false;
    Object.keys(properties).forEach((key) => {
      if (!key.startsWith('_pdp_addon_')) return;
      const propValue = String(properties[key] || '');
      const [id, qtyRaw] = propValue.split(':');
      if (String(id) === String(accessoryVariantId)) {
        const qty = parseInt(qtyRaw, 10) || 0;
        properties[key] = `${id}:${qty + 1}`;
        pdpAddonUpdated = true;
      }
    });

    if (!pdpAddonUpdated) {
      let maxIndex = 0;
      Object.keys(properties).forEach((key) => {
        if (!key.startsWith('_pdp_addon_')) return;
        const index = parseInt(key.replace('_pdp_addon_', ''), 10);
        if (!Number.isNaN(index) && index > maxIndex) maxIndex = index;
      });
      properties[`_pdp_addon_${maxIndex + 1}`] = `${accessoryVariantId}:1`;
    }

    return properties;
  }

  getDrawerPayloadBase() {
    return {
      ...TwinkleCart.getDrawerSectionPayload(),
      sections_url: window.location.pathname,
    };
  }

  async getCartState() {
    return fetch(`${routes.cart_url}.js`, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    }).then((response) => response.json());
  }

  renderCartResponse(response) {
    if (response?.sections) {
      TwinkleCart.renderDrawerResponse(response);
      return;
    }
    if (response) TwinkleCart.dispatchCartUpdate(response);
  }

  async updateParentAccessories(parentLine, parentQuantity, nextProperties) {
    await fetch(`${routes.cart_change_url}.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        line: parentLine,
        quantity: parentQuantity,
        properties: nextProperties,
      }),
    });
  }

  async addOrUpdateAddonLine(cart, accessoryVariantId, parentHandle) {
    if (!accessoryVariantId || !parentHandle) return;

    const addonIndex = cart.items.findIndex(
      (item) =>
        item.variant_id === accessoryVariantId && item.properties?._package === parentHandle
    );

    if (addonIndex >= 0) {
      const addonItem = cart.items[addonIndex];
      return fetch(`${routes.cart_change_url}.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          line: addonIndex + 1,
          quantity: addonItem.quantity + 1,
          ...this.getDrawerPayloadBase(),
        }),
      }).then((response) => response.json());
    }

    return fetch(`${routes.cart_add_url}.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        items: [
          {
            id: accessoryVariantId,
            quantity: 1,
            properties: { _addonProduct: true, _package: parentHandle },
          },
        ],
        ...this.getDrawerPayloadBase(),
      }),
    }).then((response) => response.json());
  }

  async onAccessorySubmit(event) {
    event.preventDefault();
    const form = event.currentTarget;
    if (!form || form.dataset.loading === 'true') return;

    const accessoryHandle = form.dataset.accessoryHandle;
    const accessoryVariantId = Number(form.dataset.accessoryVariantId);
    const parentLineKey = this.dataset.parentLineKey;
    const parentHandle = this.dataset.parentHandle;

    if (!accessoryHandle || !accessoryVariantId) return;

    form.dataset.loading = 'true';
    this.setButtonLoading(form, true);

    try {
      const cart = await this.getCartState();
      const parentIndex = cart.items.findIndex((item) => item.key === parentLineKey);

      if (parentIndex < 0) {
        console.warn('TwinkleCart.associated-products: parent line not found');
        return;
      }

      const parentItem = cart.items[parentIndex];
      const nextProperties = this.buildNextProperties(
        parentItem.properties,
        accessoryHandle,
        accessoryVariantId
      );

      await this.updateParentAccessories(parentIndex + 1, parentItem.quantity, nextProperties);
      const responseJson = await this.addOrUpdateAddonLine(cart, accessoryVariantId, parentHandle);
      this.renderCartResponse(responseJson);
    } catch (error) {
      console.error('TwinkleCart.associated-products: add failed', error);
    } finally {
      form.dataset.loading = 'false';
      this.setButtonLoading(form, false);
    }
  }
}

if (!customElements.get('cart-associated-products')) {
  customElements.define('cart-associated-products', CartAssociatedProducts);
}

const initAllAssociatedProducts = (root = document) => {
  root.querySelectorAll('cart-associated-products').forEach((el) => {
    if (typeof el.init === 'function') el.init();
  });
};

document.addEventListener('DOMContentLoaded', () => initAllAssociatedProducts());
document.addEventListener('cart:update', () => initAllAssociatedProducts());
